import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
  Linking,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import PantallaSegura from '../components/PantallaSegura';
import ScreenBackground from '../components/ScreenBackground';
import { useThemeColors } from '../constants/theme';
import { useAppStore } from '../store/useAppStore';
import { firestoreService } from '../services/firestoreService';
import {
  callAceptarCotizacionLogistica,
  callCancelarServicioLogistica,
  callContactoDelServicioLogistica,
} from '../services/cloudFunctions';
import { mensajeDeError } from '../utils/mensajeDeError';
import { VERTICAL_LOGISTICA_VISIBLE } from '../constants/featureFlags';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MIS TRANSPORTES — el lado del cliente
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Donde el cliente ve las cotizaciones que le llegaron, elige una, y después
 * recibe **el PIN que tiene que dictar al recibir su carga**.
 *
 * ── EL PIN ES EL CENTRO DE ESTA PANTALLA ────────────────────────────────
 *
 * «Todo debe salir con su pin de entrega y en orden porque necesitamos
 * seguridad y transparencia al hacer cada entrega», dijo el fundador. El
 * mecanismo es simple y por eso funciona: el transportista NO puede leer el
 * PIN — la regla de Firestore solo se lo deja al cliente — así que la única
 * forma de cerrar el trabajo es que el cliente se lo diga, y la única forma de
 * que el cliente se lo diga es que haya recibido su carga.
 *
 * Por eso el PIN se pinta GRANDE y con el aviso de no darlo antes de recibir.
 * Un PIN en letra pequeña se dicta por teléfono antes de que llegue el camión,
 * y entonces no protege nada.
 */

const ROTULO: Record<string, { texto: string; color: string }> = {
  ABIERTO: { texto: 'Esperando cotizaciones', color: '#D97706' },
  COTIZADO: { texto: 'Tienes cotizaciones', color: '#2563EB' },
  ACEPTADO: { texto: 'Aceptado', color: '#10B981' },
  EN_CURSO: { texto: 'En camino', color: '#10B981' },
  ENTREGADO: { texto: 'Entregado', color: '#6B7280' },
  CANCELADO: { texto: 'Cancelado', color: '#6B7280' },
};

function pesos(n: unknown): string {
  const v = Number(n ?? 0) || 0;
  return `RD$${v.toLocaleString('es-DO')}`;
}

export default function LogisticaMisPedidosScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const uid = useAppStore((s) => s.userFirebaseUid);

  const [cargando, setCargando] = React.useState(true);
  const [servicios, setServicios] = React.useState<any[]>([]);
  const [pines, setPines] = React.useState<Record<string, string | null>>({});
  const [ocupado, setOcupado] = React.useState<string | null>(null);

  const avisar = (titulo: string, texto: string) => {
    if (Platform.OS === 'web') window.alert(`${titulo}\n\n${texto}`);
    else Alert.alert(titulo, texto);
  };

  React.useEffect(() => {
    if (!uid) {
      setCargando(false);
      return;
    }
    let vivo = true;
    const parar = firestoreService.listenToServiciosDeLogistica(uid, 'CLIENTE', (lista) => {
      if (!vivo) return;
      setServicios(lista);
      setCargando(false);
    });
    return () => {
      vivo = false;
      try {
        if (typeof parar === 'function') parar();
      } catch {
        /* desmontaje */
      }
    };
  }, [uid]);

  /**
   * Un listener de PIN por cada trabajo con trato. Se abren aquí y no dentro
   * de la tarjeta porque un listener montado dentro de un `.map()` se cae y se
   * vuelve a abrir en cada repintado, y eso son lecturas facturadas por cada
   * cambio de estado de la pantalla.
   */
  React.useEffect(() => {
    const conTrato = servicios.filter((s) => ['ACEPTADO', 'EN_CURSO'].includes(String(s.estado)));
    const cortes = conTrato.map((s) =>
      firestoreService.listenToPinDeLogistica(s.id, (pin) =>
        setPines((previos) => ({ ...previos, [s.id]: pin }))
      )
    );
    return () => {
      for (const c of cortes) {
        try {
          if (typeof c === 'function') c();
        } catch {
          /* desmontaje */
        }
      }
    };
    // La dependencia es la LISTA DE IDS con trato, no `servicios`: si fuera el
    // array entero, cualquier cambio de un campo cualquiera cerraría y
    // reabriría todos los listeners.
  }, [servicios.map((s) => `${s.id}:${s.estado}`).join('|')]);

  const aceptar = async (servicioId: string, c: any) => {
    const recibe = pesos(c.monto);
    const confirmar =
      Platform.OS === 'web'
        ? window.confirm(
            `¿Aceptar a ${c.nombre ?? 'este transportista'} por ${recibe}?\n\n` +
              'Al aceptar, el trabajo queda cerrado con él y te damos tu PIN de entrega.'
          )
        : true;
    if (Platform.OS === 'web' && !confirmar) return;

    const hacer = async () => {
      setOcupado(servicioId);
      try {
        await callAceptarCotizacionLogistica({ servicioId, proveedorUid: String(c.proveedorUid) });
        avisar(
          'Trato cerrado',
          'Ya tienes tu PIN de entrega en esta pantalla. Dáselo al transportista SOLO cuando tengas tu carga delante.'
        );
      } catch (e: any) {
        avisar('No pudimos aceptar', mensajeDeError(e, 'Inténtalo otra vez.'));
      } finally {
        setOcupado(null);
      }
    };

    if (Platform.OS === 'web') {
      // Y en web SÍ se confirma. Antes este camino se saltaba la confirmación
      // por completo: un toque en la fila equivocada cerraba el trato con el
      // transportista de RD$12.000 en vez del de RD$3.000, y no hay vuelta
      // atrás — el trabajo queda con dueño y el PIN ya nació.
      if (
        window.confirm(
          `¿Aceptar a ${c.nombre ?? 'este transportista'} por ${recibe}?\n\n` +
            'Al aceptar queda cerrado con él y te damos tu PIN de entrega.'
        )
      ) {
        await hacer();
      }
      return;
    }
    Alert.alert(
      `¿Aceptar por ${recibe}?`,
      `Queda cerrado con ${c.nombre ?? 'este transportista'} y te damos tu PIN de entrega.`,
      [
        { text: 'No', style: 'cancel' },
        { text: 'Sí, aceptar', onPress: hacer },
      ]
    );
  };

  const cancelar = async (servicioId: string, conTrato = false) => {
    // Con el trato cerrado se pregunta: el transportista puede estar en camino.
    if (conTrato) {
      const texto =
        'Si lo cancelas, el transportista deja de ir. No se te cobra nada por cancelar.';
      if (Platform.OS === 'web') {
        if (!window.confirm(`¿Cancelar este transporte?\n\n${texto}`)) return;
      } else {
        const sigue = await new Promise<boolean>((resolver) =>
          Alert.alert('¿Cancelar este transporte?', texto, [
            { text: 'No', style: 'cancel', onPress: () => resolver(false) },
            { text: 'Sí, cancelar', style: 'destructive', onPress: () => resolver(true) },
          ])
        );
        if (!sigue) return;
      }
    }
    setOcupado(servicioId);
    try {
      await callCancelarServicioLogistica({ servicioId, motivo: 'El cliente lo canceló' });
    } catch (e: any) {
      avisar('No pudimos cancelarlo', mensajeDeError(e, 'Inténtalo otra vez.'));
    } finally {
      setOcupado(null);
    }
  };

  const llamar = async (servicioId: string) => {
    try {
      const r = await callContactoDelServicioLogistica({ servicioId });
      const tel = String(r?.data?.telefono ?? '').replace(/\D/g, '');
      if (!tel) {
        avisar('Sin teléfono', 'Este transportista no tiene un número guardado.');
        return;
      }
      const url = `tel:${tel}`;
      if (await Linking.canOpenURL(url)) await Linking.openURL(url);
      else avisar(r.data.nombre, tel);
    } catch (e: any) {
      avisar('No pudimos darte el contacto', mensajeDeError(e, 'Inténtalo otra vez.'));
    }
  };

  if (!VERTICAL_LOGISTICA_VISIBLE) return null;

  return (
    <ScreenBackground>
      <PantallaSegura>
        <View style={[estilos.cabecera, { borderBottomColor: colors.border }]}>
          <TouchableOpacity onPress={() => router.back()} style={estilos.salida}>
            <Ionicons name="chevron-back" size={22} color={colors.text} />
            <Text style={[estilos.salidaTexto, { color: colors.text }]}>Volver</Text>
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={estilos.cuerpo}>
          <Text style={[estilos.titulo, { color: colors.text }]}>Mis transportes</Text>

          {cargando && (
            <View style={estilos.centro}>
              <ActivityIndicator color={colors.primary} />
            </View>
          )}

          {!cargando && servicios.length === 0 && (
            <View style={[estilos.bloque, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <Text style={[estilos.bloqueTexto, { color: colors.textSecondary }]}>
                Todavía no has pedido ningún transporte de carga.
              </Text>
              <TouchableOpacity
                onPress={() => router.push('/logistica-pedir' as any)}
                style={[estilos.boton, { backgroundColor: '#EF6C00' }]}
              >
                <Text style={estilos.botonTexto}>Pedir uno</Text>
              </TouchableOpacity>
            </View>
          )}

          {servicios.map((s) => {
            const rot = ROTULO[String(s.estado)] ?? { texto: String(s.estado), color: colors.textSecondary };
            const cotizaciones: any[] = Array.isArray(s.cotizaciones) ? s.cotizaciones : [];
            const pin = pines[s.id];
            const conTrato = ['ACEPTADO', 'EN_CURSO'].includes(String(s.estado));
            return (
              <View
                key={s.id}
                style={[estilos.bloque, { backgroundColor: colors.card, borderColor: colors.border }]}
              >
                <View style={estilos.filaRotulo}>
                  <Text style={[estilos.tipo, { color: colors.text }]}>{String(s.tipo ?? '')}</Text>
                  <Text style={[estilos.estado, { color: rot.color }]}>{rot.texto}</Text>
                </View>
                <Text style={[estilos.bloqueTexto, { color: colors.textSecondary }]}>
                  {String(s.descripcion ?? '')}
                </Text>
                <Text style={[estilos.ruta, { color: colors.textSecondary }]}>
                  {String(s.origen?.texto ?? '—')}  →  {String(s.destino?.texto ?? '—')}
                </Text>

                {/* ══ EL PIN ══════════════════════════════════════════════
                    Grande, y con el aviso pegado. Un PIN en letra pequeña se
                    dicta por teléfono antes de que llegue el camión, y
                    entonces no protege nada. */}
                {conTrato && (
                  <View style={[estilos.pinCaja, { borderColor: '#10B981' }]}>
                    <Text style={[estilos.pinRotulo, { color: colors.textSecondary }]}>
                      TU PIN DE ENTREGA
                    </Text>
                    <Text style={[estilos.pin, { color: colors.text }]}>
                      {pin ?? '••••'}
                    </Text>
                    <Text style={[estilos.pinAviso, { color: '#D97706' }]}>
                      Dáselo SOLO cuando tengas tu carga delante. Es lo único que cierra el
                      trabajo.
                    </Text>
                  </View>
                )}

                {conTrato && (
                  <View style={estilos.filaBotones}>
                    <TouchableOpacity
                      onPress={() => llamar(s.id)}
                      style={[estilos.botonSuave, { borderColor: colors.border }]}
                    >
                      <Ionicons name="call-outline" size={16} color={colors.text} />
                      <Text style={[estilos.botonSuaveTexto, { color: colors.text }]}>
                        Llamar al transportista
                      </Text>
                    </TouchableOpacity>
                  </View>
                )}

                {/* Las cotizaciones recibidas */}
                {['ABIERTO', 'COTIZADO'].includes(String(s.estado)) && (
                  <>
                    {cotizaciones.length === 0 ? (
                      <Text style={[estilos.ayuda, { color: colors.textSecondary }]}>
                        Todavía nadie ha cotizado. En cuanto llegue la primera te avisamos.
                      </Text>
                    ) : (
                      cotizaciones.map((c, i) => (
                        <View key={`${c.proveedorUid}-${i}`} style={[estilos.cotizacion, { borderColor: colors.border }]}>
                          <View style={{ flex: 1 }}>
                            <Text style={[estilos.cotNombre, { color: colors.text }]}>
                              {String(c.nombre ?? 'Transportista')}
                            </Text>
                            <Text style={[estilos.cotAyuda, { color: colors.textSecondary }]}>
                              {String(c.tipoVehiculo ?? '').toLowerCase()}
                            </Text>
                          </View>
                          <Text style={[estilos.cotMonto, { color: colors.text }]}>{pesos(c.monto)}</Text>
                          <TouchableOpacity
                            onPress={() => aceptar(s.id, c)}
                            disabled={ocupado === s.id}
                            style={[estilos.aceptar, { backgroundColor: '#10B981' }]}
                          >
                            {ocupado === s.id ? (
                              <ActivityIndicator color="#FFF" size="small" />
                            ) : (
                              <Text style={estilos.aceptarTexto}>Elegir</Text>
                            )}
                          </TouchableOpacity>
                        </View>
                      ))
                    )}
                    <TouchableOpacity
                      onPress={() => cancelar(s.id)}
                      disabled={ocupado === s.id}
                      style={[estilos.botonSuave, { borderColor: colors.border, marginTop: 10 }]}
                    >
                      <Text style={[estilos.botonSuaveTexto, { color: '#DC2626' }]}>
                        Cancelar esta solicitud
                      </Text>
                    </TouchableOpacity>
                  </>
                )}

                {/* ══ Y TAMBIÉN CON EL TRATO YA CERRADO ═══════════════════════
                    El botón de cancelar estaba SOLO en ABIERTO y COTIZADO, así
                    que un trabajo ACEPTADO no lo podía cancelar ninguna de las
                    dos partes: si el transportista no aparecía, el trabajo se
                    quedaba ahí para siempre y el cliente no podía reabrirlo
                    para que lo cotizaran otros. Cancelar no devenga comisión —
                    el servidor no cobra por un trabajo que no se hizo. */}
                {conTrato && (
                  <TouchableOpacity
                    onPress={() => cancelar(s.id, true)}
                    disabled={ocupado === s.id}
                    style={[estilos.botonSuave, { borderColor: colors.border, marginTop: 10 }]}
                  >
                    <Text style={[estilos.botonSuaveTexto, { color: '#DC2626' }]}>
                      Cancelar este transporte
                    </Text>
                  </TouchableOpacity>
                )}

                {String(s.estado) === 'ENTREGADO' && (
                  <Text style={[estilos.ayuda, { color: colors.textSecondary }]}>
                    Entregado por {pesos(s.montoAcordado)}. Gracias por usar Go Safely.
                  </Text>
                )}
              </View>
            );
          })}
          <View style={{ height: 40 }} />
        </ScrollView>
      </PantallaSegura>
    </ScreenBackground>
  );
}

const estilos = StyleSheet.create({
  cabecera: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1 },
  salida: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, paddingHorizontal: 4 },
  salidaTexto: { fontSize: 16, marginLeft: 2 },
  cuerpo: { paddingHorizontal: 16, paddingTop: 8 },
  centro: { paddingVertical: 40, alignItems: 'center' },
  titulo: { fontSize: 26, fontWeight: '700', marginBottom: 12 },
  bloque: { borderRadius: 14, borderWidth: 1, padding: 16, marginBottom: 14 },
  filaRotulo: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  tipo: { fontSize: 16, fontWeight: '700' },
  estado: { fontSize: 12, fontWeight: '700' },
  bloqueTexto: { fontSize: 14, lineHeight: 21 },
  ruta: { fontSize: 13, lineHeight: 19, marginTop: 6 },
  ayuda: { fontSize: 13, lineHeight: 19, marginTop: 8 },
  pinCaja: { borderRadius: 12, borderWidth: 2, padding: 14, marginTop: 12, alignItems: 'center' },
  pinRotulo: { fontSize: 11, fontWeight: '700', letterSpacing: 1 },
  pin: { fontSize: 42, fontWeight: '800', letterSpacing: 8, marginVertical: 4 },
  pinAviso: { fontSize: 12, lineHeight: 18, textAlign: 'center', fontWeight: '600' },
  filaBotones: { marginTop: 10 },
  cotizacion: { flexDirection: 'row', alignItems: 'center', gap: 10, borderTopWidth: 1, paddingTop: 12, marginTop: 12 },
  cotNombre: { fontSize: 15, fontWeight: '600' },
  cotAyuda: { fontSize: 12, textTransform: 'capitalize' },
  cotMonto: { fontSize: 16, fontWeight: '700' },
  aceptar: { borderRadius: 10, paddingVertical: 8, paddingHorizontal: 14 },
  aceptarTexto: { color: '#FFF', fontSize: 14, fontWeight: '700' },
  boton: { borderRadius: 14, paddingVertical: 14, alignItems: 'center', marginTop: 12 },
  botonTexto: { color: '#FFF', fontSize: 16, fontWeight: '700' },
  botonSuave: { flexDirection: 'row', gap: 8, borderRadius: 12, paddingVertical: 12, alignItems: 'center', justifyContent: 'center', borderWidth: 1 },
  botonSuaveTexto: { fontSize: 14, fontWeight: '600' },
});
